// src/pages/Printing.js
import React, { useEffect, useState, useCallback } from 'react';
import { getOrders, updateStatus } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { T, fmt, Badge, Card, Btn, StatCard, Empty, Spinner, SectionTitle, TH, TD, InfoBox } from '../components/UI';

export default function Printing() {
  const { can } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getOrders();
      setOrders(r.data.data.filter(o => ['PAID','AWAITING_APPROVAL','PRINTING','PACKAGED'].includes(o.status)));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id, status) => {
    try { await updateStatus(id, status); await load(); }
    catch(e) { alert(e.response?.data?.message || 'Failed'); }
  };

  const paid     = orders.filter(o => o.status === 'PAID');
  const waiting  = orders.filter(o => o.status === 'AWAITING_APPROVAL');
  const printing = orders.filter(o => o.status === 'PRINTING');
  const packaged = orders.filter(o => o.status === 'PACKAGED');

  if (loading) return <Spinner />;

  return (
    <div>
      <SectionTitle sub="Bett authorizes · Langat executes">Printing System</SectionTitle>

      <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', marginBottom:'20px' }}>
        <StatCard label="Paid — Ready"       value={paid.length}     sub="Submit for approval" color={T.success} />
        <StatCard label="Awaiting Approval"  value={waiting.length}  sub="Bett to review" color={waiting.length>0?T.danger:T.muted} />
        <StatCard label="In Printing"        value={printing.length} color={T.info} />
        <StatCard label="Packaged"           value={packaged.length} color={T.gold} />
      </div>

      {/* Step 1: Submit for approval (sales/operations) */}
      {(can('submit_for_printing')) && paid.length > 0 && (
        <Card style={{ border:`1px solid ${T.success}33` }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.success, marginBottom:'12px' }}>✅ Step 1 — Submit Paid Orders for Printing Approval</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Items</th><th style={TH}>Amount</th><th style={TH}>Action</th></tr></thead>
            <tbody>{paid.map(o => (
              <tr key={o.id}>
                <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{o.order_number}</td>
                <td style={TD}>{o.customer_name}</td>
                <td style={{ ...TD, color:T.muted, fontSize:'12px' }}>{o.notes||'—'}</td>
                <td style={{ ...TD, fontWeight:600 }}>{fmt(o.total_amount)}</td>
                <td style={TD}><Btn size="sm" onClick={() => action(o.id, 'AWAITING_APPROVAL')}>📤 Submit for Approval</Btn></td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}

      {/* Step 2: Bett approval */}
      {can('approve_printing') && waiting.length > 0 && (
        <Card style={{ border:`1px solid ${T.danger}44` }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.danger, marginBottom:'12px' }}>🔴 Step 2 — Your Approval Required (Bett)</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Amount</th><th style={TH}>Approve</th><th style={TH}>Reject</th></tr></thead>
            <tbody>{waiting.map(o => (
              <tr key={o.id}>
                <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{o.order_number}</td>
                <td style={TD}>{o.customer_name}</td>
                <td style={{ ...TD, fontWeight:600 }}>{fmt(o.total_amount)}</td>
                <td style={TD}><Btn size="sm" variant="success" onClick={() => action(o.id, 'PRINTING')}>✅ Approve</Btn></td>
                <td style={TD}><Btn size="sm" variant="danger" onClick={() => action(o.id, 'PAID')}>❌ Reject</Btn></td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}

      {/* Waiting — read-only for others */}
      {!can('approve_printing') && waiting.length > 0 && (
        <Card style={{ border:`1px solid ${T.warning}33` }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.warning, marginBottom:'12px' }}>⏳ Awaiting Bett's Approval</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Amount</th><th style={TH}>Status</th></tr></thead>
            <tbody>{waiting.map(o => (
              <tr key={o.id}><td style={{ ...TD, color:T.gold, fontWeight:700 }}>{o.order_number}</td><td style={TD}>{o.customer_name}</td><td style={{ ...TD, fontWeight:600 }}>{fmt(o.total_amount)}</td><td style={TD}><Badge status={o.status}/></td></tr>
            ))}</tbody>
          </table>
        </Card>
      )}

      {/* Step 3: Langat printing */}
      {printing.length > 0 && (
        <Card style={{ border:`1px solid ${T.gold}33` }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.gold, marginBottom:'12px' }}>🖨️ Step 3 — Currently Printing</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Approved By</th>{can('mark_packaged')&&<th style={TH}>Action</th>}</tr></thead>
            <tbody>{printing.map(o => (
              <tr key={o.id}>
                <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{o.order_number}</td>
                <td style={TD}>{o.customer_name}</td>
                <td style={{ ...TD, color:T.success, fontSize:'12px' }}>{o.approved_by_name||'Bett'}</td>
                {can('mark_packaged')&&<td style={TD}><Btn size="sm" variant="info" onClick={() => action(o.id, 'PACKAGED')}>📦 Mark Packaged</Btn></td>}
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}

      {/* Packaged */}
      {packaged.length > 0 && (
        <Card>
          <div style={{ fontSize:'13px', fontWeight:700, color:T.success, marginBottom:'12px' }}>📦 Packaged — Ready for Dispatch</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr><th style={TH}>Order</th><th style={TH}>Customer</th><th style={TH}>Amount</th><th style={TH}>Status</th></tr></thead>
            <tbody>{packaged.map(o => (
              <tr key={o.id}><td style={{ ...TD, color:T.gold, fontWeight:700 }}>{o.order_number}</td><td style={TD}>{o.customer_name}</td><td style={{ ...TD, fontWeight:600 }}>{fmt(o.total_amount)}</td><td style={TD}><span style={{ color:T.success, fontSize:'12px' }}>✓ Ready for Logistics</span></td></tr>
            ))}</tbody>
          </table>
        </Card>
      )}

      {orders.length === 0 && <Card><Empty msg="No orders in the printing pipeline right now." /></Card>}
    </div>
  );
}
